export type Role = "user" | "admin" | "super_admin";
export type AccountType = "buyer" | "seller";
export type SellerStatus =
  | "none"
  | "pending"
  | "approved"
  | "suspended"
  | "rejected";
export type ProductStatus =
  | "active"
  | "paused"
  | "pending_moderation"
  | "sold"
  | "deleted";
export type ReportStatus = "pending" | "reviewed" | "dismissed";

export interface AdminProfile {
  id: string;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
  account_type: AccountType;
  role: Role;
  is_active: boolean;
  seller_status: SellerStatus;
  seller_verified: boolean;
  created_at: string;
}

export interface AdminProduct {
  id: number;
  seller_id: string;
  title: string;
  description: string | null;
  price: number;
  category: string | null;
  condition: "nuevo" | "usado";
  images: unknown;
  status: ProductStatus;
  currency: string | null;
  featured: boolean;
  created_at: string;
}

export interface AdminReport {
  id: number;
  product_id: number;
  reporter_id: string;
  reason: string;
  details: string | null;
  status: ReportStatus;
  created_at: string;
}

export function getProductImages(product: AdminProduct): string[] {
  const raw = product.images as unknown;
  if (!raw) return [];

  let list: unknown[] = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) list = parsed;
    } catch {
      return [];
    }
  }

  const result: string[] = [];
  for (const item of list) {
    if (typeof item === "string" && item.trim()) {
      result.push(item);
    } else if (item && typeof item === "object") {
      const obj = item as Record<string, unknown>;
      const url =
        obj.url ?? obj.path ?? obj.src ?? obj.file_url ?? obj.public_url;
      if (typeof url === "string" && url.trim()) result.push(url);
    }
  }
  return result;
}

export function productCover(product: AdminProduct): string | null {
  return getProductImages(product)[0] ?? null;
}

export function formatPrice(value: number, currency?: string | null): string {
  const formatted = Number(value).toLocaleString("es-DO");
  return currency === "USD" ? `US$${formatted}` : `RD$${formatted}`;
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "ahora";
  if (minutes < 60) return `hace ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `hace ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `hace ${days} día${days === 1 ? "" : "s"}`;
  return formatDate(iso);
}